(function () {
	// Production version of the theme attribution capture script.
	// Deploy marker: 2026-05-29 clean prod, no browser logging.
	//
	// What this script does:
	// 1. Reads query-string params from the landing URL.
	// 2. Lowercases/normalizes param keys, but preserves values exactly as sent.
	// 3. Drops Shopify/site-control params (PAGE_SPECIFIC_PARAMS and _PREFIXES).
	// 4. Stores the remaining attribution params in the np_landing_params cookie.
	// 5. Posts them to /cart/update.js so Shopify carries them into order
	//    Additional details / customAttributes at checkout. HitPath keys (hitid,
	//    hpcid, pub, c1-c3, ...) go only from the live uid<CID> click (NT-1499).
	//
	// This file intentionally captures "all non-page-specific params" instead of
	// a strict whitelist, so new marketing partner params are not silently lost.
	// It intentionally has no browser logging so production stays clean.
	//
	// Keep behavior aligned with:
	//   utilities/landing-params-contract.ts (test contract)
	//   nativepath-v2/app/lib/cookies.ts and app/lib/attribution/hitpath.ts (Hydrogen capture)
	//   scripts/hitpath/reader (the shared npHitPath core and its rules)

	var KEY_SYNONYMS = {
		// Checkout Champ and older links may send hit IDs under several names.
		// Shopify order attributes should use one canonical key: hitid.
		hit: 'hitid',
		hit_id: 'hitid',
		cc_custom_hitid: 'hitid',
		// Analytics libraries may send the anonymous user ID under old names.
		// Shopify order attributes should use one canonical key: anonymousid.
		rudderanonymousid: 'anonymousid',
		ajs_id: 'anonymousid',
	};

	// These query params control storefront behavior, filters, redirects,
	// previews, checkout, or product choices. They are not attribution.
	// Anything in this list is intentionally blocked from cart/order attributes.
	var PAGE_SPECIFIC_PARAMS = [
		'q',
		'blogs',
		'isblog',
		'filter',
		'minprice',
		'maxprice',
		'productmetafield',
		'sort',
		'cursor',
		'direction',
		'subscription',
		'title',
		'amount', // gift card denomination picker
		'selling_plan',
		'subscription_discount',
		'discount',
		'to',
		'payment',
		'skip_shop_pay', // set by our own cart checkout buttons (NT-1375)
		'checkout_url',
		'size',
		'sizes',
		'flavor',
		'flavors',
		'passwordupdated',
		'logged_in',
		// Sign in with Shop bounces back with these: a scope list and a flag, not
		// attribution.
		'shop_sign_in',
		'shop_consented_scopes',
		'country',
		'_auth',
		'redirect',
		'return_to',
		'key',
		'format',
		// GTM buy-link plumbing, not attribution.
		'items',
		'storefront',
		// GTM preview params: present only while previewing, they would otherwise land
		// on any order placed during that session.
		'gtm_debug',
		'gtm_auth',
		'gtm_preview',
		'gtm_cookies_win',
		// Shopify's own navigation, search, recommendation and preview params. A page
		// carrying only these must not replace the landing's np_landing_params.
		'variant',
		'page',
		'sort_by',
		'view',
		'preview_theme_id',
		'_ab',
		'_fd',
		'_sc',
		'_pos',
		'_sid',
		'_ss',
		'_psq',
		'oseid',
		'constraint',
	];

	// Same, for families of keys: storefront filters (filter.v.price.gte, filter.p.vendor)
	// and product-recommendation tracking (pr_prod_strat, pr_rec_id, pr_seq, ...).
	var PAGE_SPECIFIC_PREFIXES = ['filter.', 'pr_'];

	// Blocked for a different reason than PAGE_SPECIFIC_PARAMS: not "this isn't
	// attribution" but "this must not be stored or forwarded at all" (NT-1367).
	// Mirrors SENSITIVE_PARAMS in nativepath-v2 app/lib/utils/queryPreservation.ts —
	// change both together.
	var SENSITIVE_PARAMS = [
		'token',
		'access_token',
		'id_token',
		'refresh_token',
		'auth',
		'auth_token',
		'authorization',
		'sig',
		'signature',
		'hmac',
		'secret',
		'password',
		'passwd',
		'apikey',
		'api_key',
		'client_secret',
		'jwt',
		'bearer',
		'customer_id',
		'session_id',
		'sessionid',
	];

	// Shopify /cart/update.js merges attributes by key. If we omit a key that was
	// already on the cart, Shopify leaves the old value in place.
	//
	// On a fresh attributed landing, this list makes us send blank values for the
	// known attribution keys that are missing from the current URL. That prevents
	// old UTMs/click IDs from leaking into the next order.
	//
	// Unknown partner params are still supported:
	// - New unknown params in the current URL are written.
	// - Unknown params from the previous cookie are also blanked when omitted
	//   from the current landing URL.
	//
	// If an unknown value exists only on Shopify's cart and not in this cookie,
	// the script cannot know its key, so it cannot clear that one automatically.
	var KNOWN_ATTRIBUTION_KEYS_TO_CLEAR = [
		'cohort',
		'utm_source',
		'utm_medium',
		'utm_campaign',
		'utm_content',
		'utm_term',
		'gclid',
		'fbclid',
		'cjevent',
		'hitid',
		'hpcid',
		'pub',
		'c1',
		'c2',
		'c3',
		'_kx',
		'anonymousid',
	];

	// HitPath's keys on the cart: values only from a click whose uid<CID> is still live (NT-1499),
	// otherwise sent blank. URL and np_landing_params values are hints, never the value.
	var HITPATH_KEYS = ['hitid', 'hpcid', 'pub', 'c1', 'c2', 'c3', 'cid', 'hp_aid'];
	// sessionStorage: the cart token and uid<CID> cookies the cart was last checked against.
	var CART_CHECKED_KEY = 'np_hitpath_cart_checked';

	var COOKIE = 'np_landing_params';

	// Generated from scripts/hitpath/reader/np-hitpath-core.js (NT-1499); do not edit here.
	// BEGIN npHitPath core
	var npHitPathCore = (function () {
		// HitPath's uid<CID> = <hit_id>-<click_ts>-<hash>-<lpid>. Its presence is the window.
		var UID_NAME = /^uid(\d+)$/;
		var EXTRA_COOKIE = 'np_hitpath_extra';
		var HIT_KEYS = ['hit', 'hit_id', 'hitid', 'cc_custom_hitid'];
		var CID_KEYS = ['hpcid', 'cid', 'cc_custom_hpcid'];
		var PUB_KEYS = ['pub', 'hp_aid', 'cc_custom_pub'];
		var C_KEYS = { c1: ['c1', 'cc_custom_c1'], c2: ['c2', 'cc_custom_c2'], c3: ['c3', 'cc_custom_c3'] };
		var EXTRA_KEYS = PUB_KEYS.concat(C_KEYS.c1, C_KEYS.c2, C_KEYS.c3);
		// Every param HitPath owns: surfaces write these only from a resolved click, never raw.
		var GATED_KEYS = HIT_KEYS.concat(CID_KEYS, EXTRA_KEYS);

		// Scalars only: a hand-edited cookie's arrays or objects are no value.
		function str(v) {
			var t = typeof v;
			return t === 'string' || t === 'number' || t === 'boolean' ? String(v).trim() : '';
		}

		// First occurrence wins (browser precedence); values URI-decoded once.
		function parseCookieString(cookieString) {
			var out = {};
			var parts = str(cookieString).split(';');
			for (var i = 0; i < parts.length; i++) {
				var eq = parts[i].indexOf('=');
				if (eq < 0) continue;
				var name = parts[i].slice(0, eq).trim();
				if (!name || Object.prototype.hasOwnProperty.call(out, name)) continue;
				var value = parts[i].slice(eq + 1).trim();
				try { value = decodeURIComponent(value); } catch (e) {}
				out[name] = value;
			}
			return out;
		}

		// Keys lowercased: the same param arrives as hitId, HPCID, ... A link that gives one HitPath field
		// two different values is malformed, so it carries no HitPath keys at all.
		function parseQuery(search) {
			var out = {};
			var pairs = [];
			var q = str(search).replace(/^[^?]*\?/, '').replace(/#.*$/, '');
			if (!q) return out;
			var parts = q.split('&');
			for (var i = 0; i < parts.length; i++) {
				var eq = parts[i].indexOf('=');
				var k = eq < 0 ? parts[i] : parts[i].slice(0, eq);
				var v = eq < 0 ? '' : parts[i].slice(eq + 1);
				try { k = decodeURIComponent(k.replace(/\+/g, ' ')); } catch (e) {}
				try { v = decodeURIComponent(v.replace(/\+/g, ' ')); } catch (e) {}
				k = k.toLowerCase();
				pairs.push([k, v]);
				if (k && !Object.prototype.hasOwnProperty.call(out, k)) out[k] = v;
			}
			if (hitPathConflict(pairs)) {
				for (var key in out) if (Object.prototype.hasOwnProperty.call(out, key) && isHitPathKey(key)) delete out[key];
			}
			return out;
		}

		// Whether [key, value] pairs give one HitPath field (hit, cid, pub, c1-c3, any spelling) two
		// different non-empty values. An identical repeat is not a conflict.
		function hitPathConflict(pairs) {
			var groups = [HIT_KEYS, CID_KEYS, PUB_KEYS, C_KEYS.c1, C_KEYS.c2, C_KEYS.c3];
			for (var g = 0; g < groups.length; g++) {
				var seen = '';
				for (var i = 0; i < (pairs || []).length; i++) {
					var k = str(pairs[i] && pairs[i][0]).toLowerCase();
					var v = str(pairs[i] && pairs[i][1]);
					if (!v || groups[g].indexOf(k) < 0) continue;
					if (seen && seen !== v) return true;
					seen = v;
				}
			}
			return false;
		}

		// A uid whose hit id is not numeric is not a HitPath click (e.g. a cleared 'deleted').
		function parseUid(value) {
			var p = str(value).split('-');
			if (!/^\d+$/.test(p[0])) return null;
			return { hit: p[0], clicked_at: p[1] || null, lpid: p[3] || null };
		}

		// YYYYMMDDHHMMSS orders numerically; unparseable ranks last.
		function clickedAtRank(uid) {
			var n = Number(str(uid && uid.clicked_at).replace(/[^0-9]/g, ''));
			return isFinite(n) && n > 0 ? n : -1;
		}

		// Accepts health's (rs_session_id) and secure's shapes, singly or doubly encoded.
		function parseExtra(raw) {
			var s = str(raw);
			if (!s) return null;
			var obj = null;
			try { obj = JSON.parse(s); } catch (e) {
				try { obj = JSON.parse(decodeURIComponent(s)); } catch (e2) { obj = null; }
			}
			return obj && typeof obj === 'object' ? obj : null;
		}

		function first(map, keys) {
			if (!map) return '';
			for (var i = 0; i < keys.length; i++) {
				var v = str(map[keys[i]]);
				if (v) return v;
			}
			return '';
		}

		function isHitPathKey(key) {
			return GATED_KEYS.indexOf(str(key).toLowerCase()) !== -1;
		}

		// The first source that names a click (a numeric hit or cid), whole. Merging sources key by key
		// would pair one click's hit with another's pub; a ?cid=spring names no click.
		function pickHints(sources) {
			for (var i = 0; i < (sources || []).length; i++) {
				var s = lowerKeys(sources[i]);
				if (/^\d+$/.test(first(s, HIT_KEYS)) || /^\d+$/.test(first(s, CID_KEYS))) return s;
			}
			return {};
		}

		function lowerKeys(map) {
			var out = {};
			if (!map) return out;
			for (var k in map) {
				if (Object.prototype.hasOwnProperty.call(map, k)) out[String(k).toLowerCase()] = map[k];
			}
			return out;
		}

		// All of them: the first in cookie order is the OLDEST campaign, not the last click.
		function liveClicks(cookies) {
			var out = [];
			for (var name in cookies) {
				if (!Object.prototype.hasOwnProperty.call(cookies, name)) continue;
				var m = UID_NAME.exec(name);
				if (!m) continue;
				var uid = parseUid(cookies[name]);
				if (uid) out.push({ cid: m[1], uid: uid });
			}
			return out;
		}

		// uid<CID> names to read on a by-name cookie API (Shopify sandbox): those in the jar plus
		// every hinted cid, in case the jar is hidden. Hints only widen the read; resolve() decides.
		function uidCookieNames(input) {
			input = input || {};
			var names = [];
			var seen = {};
			function add(cid) {
				cid = str(cid);
				if (/^\d+$/.test(cid) && !seen[cid]) { seen[cid] = true; names.push('uid' + cid); }
			}
			var jar = parseCookieString(input.cookieString);
			for (var name in jar) {
				var m = Object.prototype.hasOwnProperty.call(jar, name) && UID_NAME.exec(name);
				if (m) add(m[1]);
			}
			var cookies = input.cookies || {};
			var sources = [lowerKeys(input.params), lowerKeys(input.attributes), parseExtra(cookies[EXTRA_COOKIE])];
			for (var i = 0; i < sources.length; i++) {
				for (var j = 0; j < CID_KEYS.length; j++) add(sources[i] && sources[i][CID_KEYS[j]]);
			}
			return names;
		}

		// np_hitpath_extra to write for this landing, or null when it names no click (a hit or cid).
		// Merges with the existing cookie only for the same click; otherwise replaces it.
		function extraFromLanding(params, existingRaw) {
			params = lowerKeys(params);
			var clip = function (v) { return str(v).substring(0, 128); };
			var landing = {
				hit: clip(first(params, HIT_KEYS)),
				hpcid: clip(first(params, CID_KEYS)),
				pub: clip(first(params, PUB_KEYS)),
				c1: clip(first(params, C_KEYS.c1)), c2: clip(first(params, C_KEYS.c2)), c3: clip(first(params, C_KEYS.c3))
			};
			// Only a numeric hit or cid names a click: not another param's `cid=spring`, nor a link placeholder.
			if (!/^\d+$/.test(landing.hit)) landing.hit = '';
			if (!/^\d+$/.test(landing.hpcid)) landing.hpcid = '';
			if (!landing.hit && !landing.hpcid) return null;
			var old = parseExtra(existingRaw);
			var same = !!old && (landing.hit
				? str(old.hit) === landing.hit
				: !!landing.hpcid && first(old, CID_KEYS) === landing.hpcid);
			var out = {};
			var keys = ['hit', 'hpcid', 'pub', 'c1', 'c2', 'c3'];
			for (var i = 0; i < keys.length; i++) {
				out[keys[i]] = landing[keys[i]] || (same ? clip(keys[i] === 'hpcid' ? first(old, CID_KEYS) : old[keys[i]]) : '');
			}
			return out;
		}

		// Whether np_hitpath_extra still names a live click (same rule as its use in resolve).
		function extraIsLive(cookies) {
			var extra = parseExtra(cookies && cookies[EXTRA_COOKIE]);
			if (!extra) return false;
			var clicks = liveClicks(cookies || {});
			for (var i = 0; i < clicks.length; i++) if (isThisClick(extra, clicks[i])) return true;
			return false;
		}

		// Whether a hint source is this click: by its hit when it names a real one, else by its cid.
		function isThisClick(map, chosen) {
			var hit = first(map, HIT_KEYS);
			if (/^\d+$/.test(hit)) return hit === chosen.uid.hit;
			var cid = first(map, CID_KEYS);
			return !!cid && cid === chosen.cid;
		}

		function byCid(clicks, cid) {
			for (var i = 0; i < clicks.length; i++) if (clicks[i].cid === cid) return clicks[i];
			return null;
		}

		function byHit(clicks, hit) {
			for (var i = 0; i < clicks.length; i++) if (clicks[i].uid.hit === hit) return clicks[i];
			return null;
		}

		// A link's hit, cid or pub that is not a number is junk (an unfilled macro such as jjhitjj). Writers
		// keep it as written so the data shows the broken link; it is never a click, and a number still
		// needs a live uid<CID>. Same shape as resolve(), source 'junk'; null when nothing is junk.
		function junkValues(params) {
			var p = lowerKeys(params);
			var out = { cid: '', hit: '', clicked_at: null, lpid: null, pub: '', c1: '', c2: '', c3: '', source: 'junk' };
			var fields = [['hit', HIT_KEYS], ['cid', CID_KEYS], ['pub', PUB_KEYS]];
			var any = false;
			for (var i = 0; i < fields.length; i++) {
				var v = first(p, fields[i][1]);
				// '-' is our own old placeholder for "no hit", not a link's value.
				if (v && v !== '-' && !/^\d+$/.test(v)) { out[fields[i][0]] = v; any = true; }
			}
			return any ? out : null;
		}

		// resolve({ cookies, params, attributes }) -> { cid, hit, clicked_at, lpid, pub, c1-c3, source } | null
		// Only a live uid<CID> resolves. Pick: cart hit, URL hit, URL cid, np_hitpath_extra cid, newest.
		// pub/c1-c3 only from params or np_hitpath_extra captured for the same click (same hit).
		function resolve(input) {
			input = input || {};
			var cookies = input.cookies || {};
			var params = lowerKeys(input.params);
			var attributes = lowerKeys(input.attributes);
			var clicks = liveClicks(cookies);
			if (!clicks.length) return null;

			var extra = parseExtra(cookies[EXTRA_COOKIE]);
			var cartHit = first(attributes, HIT_KEYS);
			var urlHit = first(params, HIT_KEYS);
			var urlCid = first(params, CID_KEYS);
			var extraCid = first(extra, CID_KEYS);

			var chosen = null;
			var source = null;
			if (cartHit) { chosen = byHit(clicks, cartHit); source = 'uid_hit'; }
			if (!chosen && urlHit) { chosen = byHit(clicks, urlHit); source = 'uid_hit'; }
			if (!chosen && urlCid) { chosen = byCid(clicks, urlCid); source = 'uid_cid'; }
			// Only while np_hitpath_extra still names that click (extraIsLive's rule): a dead one is no hint.
			if (!chosen && extraCid) {
				chosen = byCid(clicks, extraCid);
				if (chosen && !isThisClick(extra, chosen)) chosen = null;
				source = 'uid_extra';
			}
			if (!chosen) {
				chosen = clicks[0];
				for (var i = 1; i < clicks.length; i++) {
					if (clickedAtRank(clicks[i].uid) > clickedAtRank(chosen.uid)) chosen = clicks[i];
				}
				source = 'uid_newest';
			}

			var urlIsThisClick = isThisClick(params, chosen);
			var extraIsThisClick = !!extra && isThisClick(extra, chosen);
			var from = urlIsThisClick && first(params, EXTRA_KEYS) ? params : (extraIsThisClick ? extra : null);

			return {
				cid: chosen.cid,
				hit: chosen.uid.hit,
				clicked_at: chosen.uid.clicked_at,
				lpid: chosen.uid.lpid,
				pub: from ? first(from, PUB_KEYS) : '',
				c1: from ? first(from, C_KEYS.c1) : '',
				c2: from ? first(from, C_KEYS.c2) : '',
				c3: from ? first(from, C_KEYS.c3) : '',
				source: source
			};
		}

		return {
			resolve: resolve,
			junkValues: junkValues,
			pickHints: pickHints,
			isHitPathKey: isHitPathKey,
			uidCookieNames: uidCookieNames,
			extraFromLanding: extraFromLanding,
			extraIsLive: extraIsLive,
			hitPathConflict: hitPathConflict,
			parseCookieString: parseCookieString,
			parseQuery: parseQuery,
			parseUid: parseUid
		};
	})();
	// END npHitPath core

	function hasOwn(obj, key) {
		// Use hasOwnProperty safely so falsey values are still treated as present.
		return Object.prototype.hasOwnProperty.call(obj, key);
	}

	function hasPageSpecificPrefix(key) {
		for (var i = 0; i < PAGE_SPECIFIC_PREFIXES.length; i++) {
			if (key.indexOf(PAGE_SPECIFIC_PREFIXES[i]) === 0) return true;
		}
		return false;
	}

	function isBlockedParam(key) {
		// Page-control params aren't attribution; sensitive ones must never be stored.
		// `-` folds to `_` so one entry covers both spellings (`api-key`, `api_key`).
		return (
			PAGE_SPECIFIC_PARAMS.indexOf(key) >= 0 ||
			hasPageSpecificPrefix(key) ||
			SENSITIVE_PARAMS.indexOf(key.replace(/-/g, '_')) >= 0
		);
	}

	function normalizeKey(raw) {
		// Normalize keys only. Values are intentionally not lowercased or sanitized.
		if (!raw) return '';
		var trimmed = String(raw).trim();
		if (!trimmed) return '';
		var lower = trimmed.toLowerCase();
		return KEY_SYNONYMS[lower] || lower;
	}

	// Convert raw query/cookie pairs into the exact object we are willing to send
	// to Shopify. Empty values are dropped so "?utm_source=" does not wipe the
	// cookie by accident; stale clearing is handled separately below.
	function normalizeEntries(entries) {
		var collected = {};
		// A link giving one HitPath field two values is malformed: none of its HitPath keys are kept.
		var conflict = npHitPathCore.hitPathConflict(entries);
		for (var i = 0; i < entries.length; i++) {
			var key = normalizeKey(entries[i][0]);
			var value = entries[i][1];
			if (!key || !value || isBlockedParam(key)) continue;
			if (npHitPathCore.isHitPathKey(key) && (conflict || hasOwn(collected, key))) continue;
			collected[key] = value;
		}
		return Object.keys(collected).length > 0 ? collected : null;
	}

	function readCookie(name) {
		// Regex: `(?:^|; )` matches start of cookie string or immediately after `; `;
		//        `name + '=([^;]*)'` captures up to the next semicolon.
		// Example: from `foo=1; np_landing_params=%7B%7D; bar=2` captures `%7B%7D`.
		var m = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
		return m ? decodeURIComponent(m[1]) : '';
	}

	function writeCookie(name, value) {
		// SameSite=Lax keeps the cookie available across normal site navigation
		// without allowing third-party contexts to read/write it.
		var parts = [name + '=' + encodeURIComponent(value), 'Path=/', 'SameSite=Lax'];
		// Regex: `(^|\.)nativepath\.com$` matches the bare domain or any subdomain.
		// Example matches: `nativepath.com`, `shop.nativepath.com`.
		// Example non-match: `nativepath-dev2.myshopify.com`.
		if (/(^|\.)nativepath\.com$/.test(location.hostname)) {
			// Production should share attribution across nativepath.com subdomains.
			// Dev2 is a myshopify.com host, so it keeps the default host cookie.
			parts.splice(1, 0, 'Domain=.nativepath.com');
		}
		if (location.protocol === 'https:') parts.push('Secure');
		document.cookie = parts.join('; ') + ';';
	}

	function collectCurrentUrlParams() {
		// URLSearchParams handles decoding and repeated params consistently.
		// If the URL cannot be parsed, fail closed and rely on the existing cookie.
		try {
			var entries = [];
			new URL(location.href).searchParams.forEach(function (value, rawKey) {
				entries.push([rawKey, value]);
			});
			return normalizeEntries(entries);
		} catch (e) {
			return null;
		}
	}

	function readLandingParamsCookie() {
		// The cookie lets attribution survive PDP -> cart -> checkout navigation
		// after the original query string is no longer present.
		var raw = readCookie(COOKIE);
		if (!raw) return null;
		try {
			var parsed = JSON.parse(raw) || {};
			var entries = Object.keys(parsed).map(function (k) {
				return [k, parsed[k]];
			});
			var clean = normalizeEntries(entries);
			// A cookie written before a param was blocked keeps carrying it, and
			// Hydrogen reads this same `.nativepath.com` cookie into its own cart
			// attributes — so rewrite it here rather than waiting for a new landing.
			var dropped = Object.keys(parsed).some(function (k) {
				var key = normalizeKey(k);
				return parsed[k] && (!clean || !hasOwn(clean, key));
			});
			if (dropped) writeCookie(COOKIE, JSON.stringify(clean || {}));
			return clean;
		} catch (e) {
			return null;
		}
	}

	var previousParams = readLandingParamsCookie();
	var currentParams = collectCurrentUrlParams();

	if (currentParams) {
		// A new attributed landing replaces the cookie with the latest values.
		// That gives us last-touch behavior for future pages in this session.
		writeCookie(COOKIE, JSON.stringify(currentParams));
	}

	// Prefer the current URL when it has attribution. Otherwise reuse the cookie
	// so checkout still receives the original landing attribution on later pages.
	var params = currentParams || previousParams;
	if (!params) {
		syncCartClick();
		return;
	}

	// Hints: the URL's params if they name a click, else the cookie's, each whole.
	var click = npHitPathCore.resolve({
		cookies: npHitPathCore.parseCookieString(document.cookie),
		params: npHitPathCore.pickHints([currentParams, previousParams]),
	});

	// The values actually sent: params without any HitPath key, plus the live click if any. Without one,
	// a broken link's non-numeric values (junk) are kept as written so the order shows the bad link.
	var attributes = clickAttributes(click || npHitPathCore.junkValues(params));
	Object.keys(params).forEach(function (key) {
		if (!npHitPathCore.isHitPathKey(key)) attributes[key] = params[key];
	});

	// Set of keys to include in the /cart/update.js request.
	// - Active keys get their current values.
	// - Missing known/prior keys get blanks, because Shopify only clears a cart
	//   attribute when we explicitly send an empty value for that key.
	var keysToWrite = {};
	if (currentParams) {
		KNOWN_ATTRIBUTION_KEYS_TO_CLEAR.forEach(function (key) {
			keysToWrite[key] = true;
		});
		// Never present in `params`, so these always resolve to a blank value —
		// which is what clears one already written onto the cart.
		SENSITIVE_PARAMS.forEach(function (key) {
			keysToWrite[key] = true;
		});
		Object.keys(previousParams || {}).forEach(function (key) {
			keysToWrite[key] = true;
		});
	}
	Object.keys(params).forEach(function (key) {
		keysToWrite[key] = true;
	});
	Object.keys(attributes).forEach(function (key) {
		keysToWrite[key] = true;
	});
	// Every post sends the HitPath keys, blank when there is no live click, so a click that
	// expired mid-session or a stale raw cid/hp_aid is cleared too.
	HITPATH_KEYS.forEach(function (key) {
		keysToWrite[key] = true;
	});
	postCartAttributes(keysToWrite, attributes);

	function clickAttributes(click) {
		var out = {};
		if (!click) return out;
		var resolved = { hitid: click.hit, hpcid: click.cid, pub: click.pub, c1: click.c1, c2: click.c2, c3: click.c3 };
		HITPATH_KEYS.forEach(function (key) {
			if (resolved[key]) out[key] = resolved[key];
		});
		return out;
	}

	// The cart outlives the click, and cart links set HitPath attributes without this script.
	// Pages with no landing params re-check the cart only when it or the uid<CID> cookies change
	// (cart-sync.js can swap in the Hydrogen cart): it keeps a click it names that is still live,
	// else gets the newest live click, else keeps only a broken link's junk.
	function syncCartClick() {
		var jar = npHitPathCore.parseCookieString(document.cookie);
		var cartToken = jar.cart || '';
		var uids = npHitPathCore.uidCookieNames({ cookieString: document.cookie });
		// No cart and no click: nothing to write or clear, so skip the read.
		if (!cartToken && !uids.length) return;
		var checked = 'cart=' + cartToken + '|uids:' + uids
			.sort()
			.map(function (name) {
				return name + '=' + jar[name];
			})
			.join('|');
		try {
			if (sessionStorage.getItem(CART_CHECKED_KEY) === checked) return;
		} catch (e) {
			return;
		}
		fetch('/cart.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } })
			.then(function (res) {
				if (!res.ok) throw new Error('cart.js ' + res.status);
				return res.json();
			})
			.then(function (cart) {
				// cart-sync.js swapped carts during the read: these values were for the other one.
				if ((npHitPathCore.parseCookieString(document.cookie).cart || '') !== cartToken) return;
				var attrs = (cart && cart.attributes) || {};
				// No live click: the cart keeps a broken link's junk; a number without a live uid is cleared.
				var live = npHitPathCore.resolve({ cookies: jar, attributes: attrs }) || npHitPathCore.junkValues(attrs);
				var values = clickAttributes(live);
				var cartCid = attrs.hpcid || attrs.cid;
				if (live && (attrs.hitid ? live.hit === attrs.hitid : live.cid === cartCid)) {
					// The same click keeps the cart's pub/c1-c3, written when it landed (as Hydrogen does).
					['pub', 'c1', 'c2', 'c3'].forEach(function (key) {
						if (attrs[key]) values[key] = attrs[key];
						else delete values[key];
					});
				}
				// Write only the HitPath keys that differ; a key missing from values is blanked.
				var keys = {};
				HITPATH_KEYS.forEach(function (key) {
					if ((attrs[key] || '') !== (values[key] || '')) keys[key] = true;
				});
				// Any other HitPath key on the cart (the old asset's raw cc_custom_* twins) is blanked too.
				Object.keys(attrs).forEach(function (key) {
					if (attrs[key] && npHitPathCore.isHitPathKey(key) && !hasOwn(values, key)) keys[key] = true;
				});
				// Flag the cart only once it is right, so a failed read or write (a 429) is retried on a later page.
				if (!Object.keys(keys).length) return flagChecked();
				postCartAttributes(keys, values).then(function (ok) {
					if (ok) flagChecked();
				});
			})
			.catch(function () {});

		function flagChecked() {
			try {
				sessionStorage.setItem(CART_CHECKED_KEY, checked);
			} catch (e) {}
		}
	}

	function postCartAttributes(keysToWrite, attributes) {
		var body = [];
		Object.keys(keysToWrite).forEach(function (key) {
			// If the param is present, write its exact value. If it is stale, send an
			// empty value so Shopify clears the cart attribute.
			var value = hasOwn(attributes, key) ? attributes[key] : '';
			body.push('attributes[' + encodeURIComponent(key) + ']=' + encodeURIComponent(value));
		});

		return fetch('/cart/update.js', {
			method: 'POST',
			headers: {
				'Content-Type': 'application/x-www-form-urlencoded',
				Accept: 'application/json',
			},
			body: body.join('&'),
			// Shopify identifies the current cart through same-origin cart cookies.
			// Including credentials ties this update to the shopper's active cart.
			credentials: 'same-origin',
		})
			.then(function (res) {
				return res.ok;
			})
			.catch(function () {
				// Production stays silent. A cart update failure should not block the page.
				return false;
			});
	}
})();
